import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Cake, ClipboardText, Check } from "@phosphor-icons/react";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  submitted: "bg-sky-100 text-sky-800",
  approved: "bg-emerald-100 text-emerald-800",
  acknowledged: "bg-emerald-100 text-emerald-800",
};

export default function ReviewsPage() {
  const { user, hasPerm } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [notes, setNotes] = useState("");
  const [approveComment, setApproveComment] = useState("");
  const [busy, setBusy] = useState(false);

  const canManage = hasPerm("users.manage");

  const load = useCallback(() => {
    if (hasPerm("users.manage")) api.get("/milestones").then((r) => setMilestones(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const managers = users.filter((u) => u.is_active && (u.role === "super_admin" || (u.permission_overrides || {})["users.manage"] === true));
  const reviewers = users.filter((u) => u.is_active && (u.role === "super_admin" || (u.permission_overrides || {})["reviews.complete"] === true));

  const adminName = (id) => users.find((a) => a.id === id)?.full_name || "—";

  const assign = async (kind, id, assigned_to_id) => {
    try {
      await api.put(`/${kind}/${id}/assign`, { assigned_to_id });
      toast.success("Assigned — they've been notified by email");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    }
  };

  const acknowledge = async (id) => {
    try {
      await api.post(`/milestones/${id}/acknowledge`);
      toast.success("Milestone acknowledged — daily reminders stopped");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const openReview = (r) => {
    setOpen(r);
    setAnswers(r.questions.map((q) => q.answer));
    setNotes(r.notes || "");
    setApproveComment("");
  };

  const saveDraft = async (silent = false) => {
    try {
      await api.put(`/reviews/${open.id}`, { answers, notes });
      if (!silent) toast.success("Draft saved");
      load();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
      return false;
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (!(await saveDraft(true))) return;
      await api.post(`/reviews/${open.id}/submit`);
      toast.success("Review submitted — sent to super admin for final review");
      setOpen(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/reviews/${open.id}/approve`, { comment: approveComment });
      toast.success("Review approved");
      setOpen(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const pendingMilestones = milestones.filter((m) => m.status === "pending");
  const acknowledged = milestones.filter((m) => m.status === "acknowledged");
  const isEditable = open && open.status === "pending" && (open.assigned_to_id === user.id || user.role === "super_admin");
  const canSubmit = open && open.status === "pending" && open.assigned_to_id === user.id;

  return (
    <div className="space-y-8">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">People Ops</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Milestones & reviews</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Work-anniversary milestones remind their assigned admin daily until acknowledged. Annual performance reviews open on each member's hire-date anniversary (super admins are excluded) — each is completed by its assigned reviewer and finalized by the super admin. Reviewers only see the reviews assigned to them.</p>
      </div>

      {canManage && (
      <div className="bg-card border border-border rounded-md">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <Cake size={19} weight="duotone" className="text-indigo-600" />
          <h3 className="font-heading font-semibold tracking-tight">Milestones {pendingMilestones.length > 0 && <Badge className="bg-amber-100 text-amber-800 ml-2">{pendingMilestones.length} pending</Badge>}</h3>
        </div>
        <div className="divide-y divide-border" data-testid="milestones-list">
          {pendingMilestones.map((m) => (
            <div key={m.id} data-testid={`milestone-row-${m.id}`} className="px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{m.user_name} — <span className="text-indigo-700 font-semibold">{m.years}-year anniversary</span></p>
                <p className="text-xs text-muted-foreground">{fmtDate(m.milestone_date)} · daily reminders to {adminName(m.assigned_to_id)} until acknowledged</p>
              </div>
              <Select value={m.assigned_to_id || ""} onValueChange={(v) => assign("milestones", m.id, v)}>
                <SelectTrigger data-testid={`milestone-assign-${m.id}`} className="w-44 h-9"><SelectValue placeholder="Assign admin" /></SelectTrigger>
                <SelectContent>{managers.map((a) => <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid={`milestone-ack-${m.id}`} size="sm" onClick={() => acknowledge(m.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Check size={15} className="mr-1" /> Acknowledge
              </Button>
            </div>
          ))}
          {pendingMilestones.length === 0 && <p className="px-6 py-6 text-sm text-muted-foreground">No pending milestones. They appear automatically as work anniversaries (1, 3, 5, 10… years) approach.</p>}
          {acknowledged.slice(0, 5).map((m) => (
            <div key={m.id} className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 opacity-70">
              <p className="text-sm">{m.user_name} — {m.years}-year anniversary ({fmtDate(m.milestone_date)})</p>
              <Badge className={STATUS_STYLE.acknowledged}>acknowledged by {m.acknowledged_by_name}</Badge>
            </div>
          ))}
        </div>
      </div>
      )}

      <div className="bg-card border border-border rounded-md">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <ClipboardText size={19} weight="duotone" className="text-indigo-600" />
          <h3 className="font-heading font-semibold tracking-tight">Annual performance reviews</h3>
        </div>
        <div className="divide-y divide-border" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`review-row-${r.id}`} className="px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{r.user_name} — {r.cycle_year} review</p>
                <p className="text-xs text-muted-foreground">Due {fmtDate(r.due_date)} · assigned to {adminName(r.assigned_to_id)}{r.submitted_by_name && ` · submitted by ${r.submitted_by_name}`}</p>
              </div>
              <Badge className={STATUS_STYLE[r.status]}>{r.status}</Badge>
              {r.status === "pending" && canManage && (
                <Select value={r.assigned_to_id || ""} onValueChange={(v) => assign("reviews", r.id, v)}>
                  <SelectTrigger data-testid={`review-assign-${r.id}`} className="w-44 h-9"><SelectValue placeholder="Assign reviewer" /></SelectTrigger>
                  <SelectContent>{reviewers.map((a) => <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Button data-testid={`review-open-${r.id}`} size="sm" variant="outline" onClick={() => openReview(r)}>Open</Button>
            </div>
          ))}
          {reviews.length === 0 && <p className="px-6 py-6 text-sm text-muted-foreground">{canManage ? "Reviews are generated automatically on each member's hire-date anniversary (super admins excluded). Members must have a hire date on their profile." : "No reviews are assigned to you right now."}</p>}
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="review-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">{open?.user_name} — {open?.cycle_year} performance review</DialogTitle>
          </DialogHeader>
          {open && (
            <div className="space-y-5">
              <p className="text-xs rounded-md bg-indigo-50 border border-indigo-200 text-indigo-900 px-3 py-2.5">
                Hold a one-on-one meeting with {open.user_name?.split(" ")[0]}. Ask each question, record their answers, and capture meeting notes. Submitting sends it to the super admin for final review.
              </p>
              {open.questions.map((q, i) => (
                <div key={i}>
                  <Label className="text-sm">{i + 1}. {q.question}</Label>
                  <Textarea data-testid={`review-answer-${i}`} className="mt-1.5" rows={2}
                    value={answers[i] || ""} disabled={!isEditable}
                    onChange={(e) => setAnswers(answers.map((a, j) => (j === i ? e.target.value : a)))} />
                </div>
              ))}
              <div>
                <Label className="text-sm font-semibold">Meeting notes</Label>
                <Textarea data-testid="review-notes" className="mt-1.5" rows={4} value={notes} disabled={!isEditable}
                  onChange={(e) => setNotes(e.target.value)} placeholder="Overall impressions, agreements made, follow-ups…" />
              </div>
              {open.status === "pending" && isEditable && (
                <div className="flex gap-2">
                  <Button data-testid="review-save-btn" variant="outline" onClick={() => saveDraft()} disabled={busy}>Save draft</Button>
                  {canSubmit && (
                    <Button data-testid="review-submit-btn" onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                      {busy ? "Submitting…" : "Submit to super admin"}
                    </Button>
                  )}
                </div>
              )}
              {open.status === "pending" && !isEditable && (
                <p className="text-xs text-muted-foreground">Only the assigned admin ({adminName(open.assigned_to_id)}) can fill in and submit this review.</p>
              )}
              {open.status === "submitted" && user.role === "super_admin" && (
                <div className="border-t border-border pt-4 space-y-3">
                  <Label className="text-sm font-semibold">Super admin comment</Label>
                  <Textarea data-testid="approve-comment" rows={2} value={approveComment} onChange={(e) => setApproveComment(e.target.value)} />
                  <Button data-testid="review-approve-btn" onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {busy ? "Approving…" : "Approve review"}
                  </Button>
                </div>
              )}
              {open.status === "submitted" && user.role !== "super_admin" && (
                <p className="text-xs text-muted-foreground">Submitted by {open.submitted_by_name} — awaiting super admin approval.</p>
              )}
              {open.status === "approved" && (
                <p className="text-sm rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2.5">
                  Approved by {open.approved_by_name} on {fmtDate(open.approved_at)}.{open.super_admin_comment && ` "${open.super_admin_comment}"`}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
